import React, { useState } from 'react'
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Copy, Sparkles } from 'lucide-react'
import {
  ProductCustomizationOptions,
  ProductOptionGroup,
  ProductOption,
  OptionGroupType,
} from '@/types'

interface CustomizationOptionsEditorProps {
  value: ProductCustomizationOptions | null
  onChange: (value: ProductCustomizationOptions | null) => void
}

// Template predefiniti per configurazioni comuni
const TEMPLATES = {
  burger: {
    name: 'Burger Personalizzabile',
    optionGroups: [
      {
        id: crypto.randomUUID(),
        name: 'Cottura',
        type: OptionGroupType.SINGLE_CHOICE,
        required: true,
        minSelections: 1,
        maxSelections: 1,
        displayOrder: 0,
        options: [
          { id: crypto.randomUUID(), name: 'Al sangue', priceModifier: 0, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Media', priceModifier: 0, available: true, displayOrder: 1 },
          { id: crypto.randomUUID(), name: 'Ben cotta', priceModifier: 0, available: true, displayOrder: 2 },
        ],
      },
      {
        id: crypto.randomUUID(),
        name: 'Salse',
        type: OptionGroupType.MULTIPLE_CHOICE,
        required: false,
        minSelections: 0,
        maxSelections: 3,
        displayOrder: 1,
        options: [
          { id: crypto.randomUUID(), name: 'Ketchup', priceModifier: 0, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Maionese', priceModifier: 0, available: true, displayOrder: 1 },
          { id: crypto.randomUUID(), name: 'BBQ', priceModifier: 0.5, available: true, displayOrder: 2 },
          { id: crypto.randomUUID(), name: 'Piccante', priceModifier: 0.5, available: true, displayOrder: 3 },
        ],
      },
      {
        id: crypto.randomUUID(),
        name: 'Extra',
        type: OptionGroupType.QUANTITY,
        required: false,
        displayOrder: 2,
        options: [
          { id: crypto.randomUUID(), name: 'Bacon', priceModifier: 1.5, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Formaggio', priceModifier: 1, available: true, displayOrder: 1 },
          { id: crypto.randomUUID(), name: 'Cipolla caramellata', priceModifier: 1, available: true, displayOrder: 2 },
        ],
      },
    ],
  },
  pizza: {
    name: 'Pizza Personalizzabile',
    optionGroups: [
      {
        id: crypto.randomUUID(),
        name: 'Dimensione',
        type: OptionGroupType.SINGLE_CHOICE,
        required: true,
        minSelections: 1,
        maxSelections: 1,
        displayOrder: 0,
        options: [
          { id: crypto.randomUUID(), name: 'Piccola (25cm)', priceModifier: -2, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Media (30cm)', priceModifier: 0, available: true, displayOrder: 1, isDefault: true },
          { id: crypto.randomUUID(), name: 'Grande (35cm)', priceModifier: 3, available: true, displayOrder: 2 },
          { id: crypto.randomUUID(), name: 'Family (45cm)', priceModifier: 6, available: true, displayOrder: 3 },
        ],
      },
      {
        id: crypto.randomUUID(),
        name: 'Ingredienti Extra',
        type: OptionGroupType.MULTIPLE_CHOICE,
        required: false,
        minSelections: 0,
        maxSelections: 5,
        displayOrder: 1,
        options: [
          { id: crypto.randomUUID(), name: 'Mozzarella extra', priceModifier: 1.5, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Prosciutto', priceModifier: 2, available: true, displayOrder: 1 },
          { id: crypto.randomUUID(), name: 'Funghi', priceModifier: 1.5, available: true, displayOrder: 2 },
          { id: crypto.randomUUID(), name: 'Olive', priceModifier: 1, available: true, displayOrder: 3 },
          { id: crypto.randomUUID(), name: 'Peperoni', priceModifier: 1.5, available: true, displayOrder: 4 },
        ],
      },
      {
        id: crypto.randomUUID(),
        name: 'Rimuovi Ingredienti',
        type: OptionGroupType.MULTIPLE_CHOICE,
        required: false,
        displayOrder: 2,
        options: [
          { id: crypto.randomUUID(), name: 'Senza pomodoro', priceModifier: 0, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Senza mozzarella', priceModifier: 0, available: true, displayOrder: 1 },
        ],
      },
    ],
  },
  beverage: {
    name: 'Bevanda Personalizzabile',
    optionGroups: [
      {
        id: crypto.randomUUID(),
        name: 'Dimensione',
        type: OptionGroupType.SINGLE_CHOICE,
        required: true,
        minSelections: 1,
        maxSelections: 1,
        displayOrder: 0,
        options: [
          { id: crypto.randomUUID(), name: 'Piccola', priceModifier: -1, available: true, displayOrder: 0 },
          { id: crypto.randomUUID(), name: 'Media', priceModifier: 0, available: true, displayOrder: 1, isDefault: true },
          { id: crypto.randomUUID(), name: 'Grande', priceModifier: 1.5, available: true, displayOrder: 2 },
        ],
      },
      {
        id: crypto.randomUUID(),
        name: 'Ghiaccio',
        type: OptionGroupType.SINGLE_CHOICE,
        required: false,
        displayOrder: 1,
        options: [
          { id: crypto.randomUUID(), name: 'Con ghiaccio', priceModifier: 0, available: true, displayOrder: 0, isDefault: true },
          { id: crypto.randomUUID(), name: 'Senza ghiaccio', priceModifier: 0, available: true, displayOrder: 1 },
          { id: crypto.randomUUID(), name: 'Extra ghiaccio', priceModifier: 0, available: true, displayOrder: 2 },
        ],
      },
    ],
  },
}

export default function CustomizationOptionsEditor({
  value,
  onChange,
}: CustomizationOptionsEditorProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [showTemplates, setShowTemplates] = useState(false)

  const enabled = value !== null

  const toggleEnabled = () => {
    if (enabled) {
      onChange(null)
    } else {
      onChange({
        optionGroups: [],
      })
    }
  }

  const applyTemplate = (templateKey: keyof typeof TEMPLATES) => {
    const template = TEMPLATES[templateKey]
    onChange({
      optionGroups: template.optionGroups,
    })
    setShowTemplates(false)
    // Expand all groups after applying template
    const groupIds = template.optionGroups.map(g => g.id)
    setExpandedGroups(new Set(groupIds))
  }

  const addOptionGroup = () => {
    if (!value) return

    const newGroup: ProductOptionGroup = {
      id: crypto.randomUUID(),
      name: 'Nuovo Gruppo',
      type: OptionGroupType.SINGLE_CHOICE,
      required: false,
      displayOrder: value.optionGroups.length,
      options: [],
    }

    onChange({
      ...value,
      optionGroups: [...value.optionGroups, newGroup],
    })

    // Expand the new group
    setExpandedGroups(new Set([...expandedGroups, newGroup.id]))
  }

  const updateOptionGroup = (groupId: string, updates: Partial<ProductOptionGroup>) => {
    if (!value) return

    onChange({
      ...value,
      optionGroups: value.optionGroups.map(g =>
        g.id === groupId ? { ...g, ...updates } : g
      ),
    })
  }

  const deleteOptionGroup = (groupId: string) => {
    if (!value) return

    onChange({
      ...value,
      optionGroups: value.optionGroups
        .filter(g => g.id !== groupId)
        .map((g, index) => ({ ...g, displayOrder: index })),
    })

    // Remove from expanded
    const newExpanded = new Set(expandedGroups)
    newExpanded.delete(groupId)
    setExpandedGroups(newExpanded)
  }

  const duplicateOptionGroup = (group: ProductOptionGroup) => {
    if (!value) return

    const duplicated: ProductOptionGroup = {
      ...JSON.parse(JSON.stringify(group)),
      id: crypto.randomUUID(),
      name: `${group.name} (Copia)`,
      displayOrder: value.optionGroups.length,
      options: group.options.map(opt => ({
        ...opt,
        id: crypto.randomUUID(),
      })),
    }

    onChange({
      ...value,
      optionGroups: [...value.optionGroups, duplicated],
    })

    setExpandedGroups(new Set([...expandedGroups, duplicated.id]))
  }

  const addOption = (groupId: string) => {
    if (!value) return

    const group = value.optionGroups.find(g => g.id === groupId)
    if (!group) return

    const newOption: ProductOption = {
      id: crypto.randomUUID(),
      name: 'Nuova Opzione',
      priceModifier: 0,
      available: true,
      displayOrder: group.options.length,
    }

    updateOptionGroup(groupId, {
      options: [...group.options, newOption],
    })
  }

  const updateOption = (
    groupId: string,
    optionId: string,
    updates: Partial<ProductOption>
  ) => {
    if (!value) return

    const group = value.optionGroups.find(g => g.id === groupId)
    if (!group) return

    updateOptionGroup(groupId, {
      options: group.options.map(opt =>
        opt.id === optionId ? { ...opt, ...updates } : opt
      ),
    })
  }

  const deleteOption = (groupId: string, optionId: string) => {
    if (!value) return

    const group = value.optionGroups.find(g => g.id === groupId)
    if (!group) return

    updateOptionGroup(groupId, {
      options: group.options
        .filter(opt => opt.id !== optionId)
        .map((opt, index) => ({ ...opt, displayOrder: index })),
    })
  }

  const toggleGroupExpanded = (groupId: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId)
    } else {
      newExpanded.add(groupId)
    }
    setExpandedGroups(newExpanded)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🎨</span>
            Opzioni di Customizzazione
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Permetti ai clienti di personalizzare questo prodotto (es. dimensione, ingredienti, extra)
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggleEnabled}
            className="w-5 h-5 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:bg-gray-700"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Abilita customizzazione
          </span>
        </label>
      </div>

      {!enabled && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            La customizzazione è disabilitata. Attiva l'opzione sopra per permettere ai clienti di personalizzare questo prodotto.
          </p>
        </div>
      )}

      {enabled && value && (
        <>
          {/* Templates Button */}
          {value.optionGroups.length === 0 && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border-2 border-purple-200 dark:border-purple-800 rounded-lg hover:border-purple-400 dark:hover:border-purple-600 transition-all flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="font-semibold text-purple-900 dark:text-purple-100">
                  {showTemplates ? 'Nascondi Template' : 'Usa un Template Predefinito'}
                </span>
              </button>

              {showTemplates && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(TEMPLATES).map(([key, template]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyTemplate(key as keyof typeof TEMPLATES)}
                      className="p-4 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-all text-left"
                    >
                      <div className="font-semibold text-gray-900 dark:text-white mb-2">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {template.optionGroups.map((group, idx) => (
                          <div key={idx}>• {group.name} ({group.options.length} opzioni)</div>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Option Groups */}
          <div className="space-y-3">
            {value.optionGroups
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((group, groupIndex) => {
                const isExpanded = expandedGroups.has(group.id)

                return (
                  <div
                    key={group.id}
                    className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden"
                  >
                    {/* Group Header */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex items-center gap-3">
                      <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />

                      <button
                        type="button"
                        onClick={() => toggleGroupExpanded(group.id)}
                        className="flex-1 flex items-center gap-3 text-left"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}

                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {group.name}
                            {group.required && <span className="text-red-500 ml-1">*</span>}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {group.type} • {group.options.length} opzioni
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => duplicateOptionGroup(group)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                        title="Duplica gruppo"
                      >
                        <Copy className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteOptionGroup(group.id)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition"
                        title="Elimina gruppo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Group Content */}
                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        {/* Group Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Nome Gruppo *
                            </label>
                            <input
                              type="text"
                              value={group.name}
                              onChange={(e) => updateOptionGroup(group.id, { name: e.target.value })}
                              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:text-white"
                              placeholder="es. Dimensione, Salse, Extra"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Tipo *
                            </label>
                            <select
                              value={group.type}
                              onChange={(e) =>
                                updateOptionGroup(group.id, {
                                  type: e.target.value as OptionGroupType,
                                })
                              }
                              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:text-white"
                            >
                              <option value={OptionGroupType.SINGLE_CHOICE}>
                                Scelta Singola (radio)
                              </option>
                              <option value={OptionGroupType.MULTIPLE_CHOICE}>
                                Scelta Multipla (checkbox)
                              </option>
                              <option value={OptionGroupType.QUANTITY}>
                                Quantità (numero)
                              </option>
                            </select>
                          </div>

                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={group.required}
                                onChange={(e) =>
                                  updateOptionGroup(group.id, { required: e.target.checked })
                                }
                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:bg-gray-700"
                              />
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Obbligatorio
                              </span>
                            </label>
                          </div>

                          {group.type === OptionGroupType.MULTIPLE_CHOICE && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Min. Selezioni
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={group.minSelections || 0}
                                  onChange={(e) =>
                                    updateOptionGroup(group.id, {
                                      minSelections: parseInt(e.target.value) || 0,
                                    })
                                  }
                                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:text-white"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Max. Selezioni
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  value={group.maxSelections || ''}
                                  onChange={(e) =>
                                    updateOptionGroup(group.id, {
                                      maxSelections: parseInt(e.target.value) || undefined,
                                    })
                                  }
                                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:text-white"
                                  placeholder="Illimitate"
                                />
                              </div>
                            </>
                          )}
                        </div>

                        {/* Options */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              Opzioni
                            </label>
                            <button
                              type="button"
                              onClick={() => addOption(group.id)}
                              className="text-xs px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Aggiungi Opzione
                            </button>
                          </div>

                          {group.options.length === 0 ? (
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-center text-xs text-gray-500 dark:text-gray-400">
                              Nessuna opzione. Clicca "Aggiungi Opzione" per iniziare.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {group.options
                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                .map((option) => (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                                  >
                                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />

                                    <input
                                      type="text"
                                      value={option.name}
                                      onChange={(e) =>
                                        updateOption(group.id, option.id, {
                                          name: e.target.value,
                                        })
                                      }
                                      className="flex-1 px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:text-white"
                                      placeholder="Nome opzione"
                                    />

                                    <input
                                      type="number"
                                      step="0.01"
                                      value={option.priceModifier}
                                      onChange={(e) =>
                                        updateOption(group.id, option.id, {
                                          priceModifier: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      className="w-24 px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:text-white"
                                      placeholder="±€"
                                    />

                                    {group.type === OptionGroupType.SINGLE_CHOICE && (
                                      <label className="flex items-center gap-1 cursor-pointer" title="Opzione predefinita">
                                        <input
                                          type="checkbox"
                                          checked={option.isDefault || false}
                                          onChange={(e) =>
                                            updateOption(group.id, option.id, {
                                              isDefault: e.target.checked,
                                            })
                                          }
                                          className="w-3.5 h-3.5 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:bg-gray-700"
                                        />
                                        <span className="text-xs text-gray-600 dark:text-gray-400">Default</span>
                                      </label>
                                    )}

                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={option.available}
                                        onChange={(e) =>
                                          updateOption(group.id, option.id, {
                                            available: e.target.checked,
                                          })
                                        }
                                        className="w-3.5 h-3.5 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 dark:bg-gray-700"
                                      />
                                      <span className="text-xs text-gray-600 dark:text-gray-400">Disp.</span>
                                    </label>

                                    <button
                                      type="button"
                                      onClick={() => deleteOption(group.id, option.id)}
                                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition flex-shrink-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>

          {/* Add Group Button */}
          <button
            type="button"
            onClick={addOptionGroup}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Aggiungi Gruppo di Opzioni</span>
          </button>

          {/* Info Box */}
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
              <span>💡</span>
              <span>
                <strong>Tip:</strong> I clienti potranno vedere il prezzo finale aggiornato in tempo reale mentre customizzano il prodotto.
                Usa i modificatori di prezzo (es. +1.50, -0.50) per gestire i supplementi.
              </span>
            </p>
          </div>
        </>
      )}
    </div>
  )
}
